import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import { useBreadcrumbContext } from '../context/BreadcrumbContext';

const DURATION = 150;

/** @type {import('react').FC<import('../lib/propTypes').BreadcrumbContentProps>} */

const BreadcrumbContent = ({ className, children }) => {
  const { current, step, direction } = useBreadcrumbContext();
  const content = current?.content ?? children;

  // 'idle' | 'leaving' | 'enter-start' | 'enter-active'
  const [animState, setAnimState] = useState('idle');
  const [outgoing, setOutgoing] = useState(null);
  const [incoming, setIncoming] = useState(content);
  const prevStepRef = useRef(step);
  const prevContentRef = useRef(content);
  const timeoutRef = useRef(null);
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    if (prevStepRef.current === step) {
      setIncoming(content);
      prevContentRef.current = content;
      return;
    }

    const prevContent = prevContentRef.current;
    prevStepRef.current = step;
    prevContentRef.current = content;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    // Phase 1: Leave — old content slides out
    setOutgoing(prevContent);
    setIncoming(null);
    setAnimState('leaving');

    timeoutRef.current = setTimeout(() => {
      // Phase 2: Enter start — mount new content at displaced position
      setOutgoing(null);
      setIncoming(content);
      setAnimState('enter-start');

      // Phase 3: Enter active — next frame, animate to final position
      rafRef.current = requestAnimationFrame(() => {
        setAnimState('enter-active');

        timeoutRef.current = setTimeout(() => {
          setAnimState('idle');
        }, DURATION);
      });
    }, DURATION);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [step, content]);

  if (!outgoing && !incoming) return null;

  const isForward = direction === 'forward';

  const leaveTarget = isForward ? '-translate-x-16 opacity-0' : 'translate-x-16 opacity-0';
  const enterFrom = isForward ? 'translate-x-16 opacity-0' : '-translate-x-16 opacity-0';
  const enterTo = 'translate-x-0 opacity-100';

  const getClass = () => {
    switch (animState) {
      case 'leaving':
        return leaveTarget;
      case 'enter-start':
        return enterFrom;
      case 'enter-active':
        return enterTo;
      default:
        return enterTo;
    }
  };

  return (
    <div className={cn('relative', className)}>
      {outgoing && animState === 'leaving' && (
        <div
          className={cn('transition-all ease-out', leaveTarget)}
          style={{ transitionDuration: `${DURATION}ms` }}>
          {outgoing}
        </div>
      )}
      {incoming && (
        <div
          className={cn(
            animState === 'enter-start' || animState === 'enter-active'
              ? 'transition-all ease-out'
              : '',
            getClass()
          )}
          style={
            animState === 'enter-start' || animState === 'enter-active'
              ? { transitionDuration: `${DURATION}ms` }
              : undefined
          }>
          {incoming}
        </div>
      )}
    </div>
  );
};

export default BreadcrumbContent;
